import { useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { Avatar, Tooltip } from '../ui/index.js';
import { personLabel } from '../lib/format.js';

/**
 * Who else has this ticket open.
 *
 * The collision worth preventing is two agents answering the same request at
 * once: the second one's work is wasted entirely, and the requester gets two
 * different replies from one desk.
 *
 * ONE REQUEST DOES BOTH. The beat that says "I am here" returns everybody
 * else who is, so the busiest polling endpoint in the product is not asked
 * the same question twice. It costs a small write and an indexed read — no
 * model call, nothing that bills. That distinction matters here because an
 * earlier polling feature on this screen quietly re-ran an AI summary every
 * thirty seconds.
 *
 * SILENT WHEN ALONE. Almost every ticket is being read by one person, and a
 * strip that says "nobody else is viewing" on all of them is furniture. The
 * component renders nothing at all until somebody else turns up.
 */
interface Viewer {
  oid: string;
  name: string | null;
  email: string | null;
  secondsAgo: number;
  arrived: boolean;
}

/**
 * Faster than the server's 45-second window, so a viewer survives one missed
 * beat rather than flickering out because a single request was slow.
 */
const BEAT_MS = 20_000;

export function ViewingNow({
  tenant,
  number,
  viewers,
  onViewers,
}: {
  tenant: string;
  number: number;
  viewers: Viewer[];
  onViewers: (v: Viewer[]) => void;
}) {
  const beat = useMutation({
    mutationFn: () =>
      api.post<{ viewers: Viewer[] }>(`/api/v1/tenants/${tenant}/tickets/${number}/viewing`, {}),
    onSuccess: (d) => onViewers(d.viewers),
    // Presence failing must never surface. It is a courtesy on a screen whose
    // job is the ticket, and an error toast about it would be worse than the
    // missing information.
    onError: () => undefined,
  });

  useEffect(() => {
    if (!tenant || !Number.isFinite(number)) return;
    beat.mutate();
    const id = setInterval(() => beat.mutate(), BEAT_MS);

    return () => {
      clearInterval(id);
      // Best effort only: it makes the others' screens right sooner than the
      // 45-second window, and the window is what makes this correct. A
      // navigation that cancels this request costs nothing.
      void api.del(`/api/v1/tenants/${tenant}/tickets/${number}/viewing`).catch(() => undefined);
      onViewers([]);
    };
    // Re-subscribe when the ticket changes, not when the mutation identity does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant, number]);

  if (viewers.length === 0) return null;

  return (
    <span className="flex items-center gap-1.5">
      <span className="flex -space-x-1.5">
        {viewers.slice(0, 4).map((v) => (
          <Tooltip
            key={v.oid}
            content={`${personLabel(v.name, v.email)} is also viewing${v.arrived ? ' — just opened it' : ''}`}
          >
            <span className="rounded-full ring-2 ring-surface">
              <Avatar name={v.name} email={v.email} size="xs" />
            </span>
          </Tooltip>
        ))}
      </span>
      <span className="text-2xs text-ink-secondary">
        {viewers.length === 1
          ? `${personLabel(viewers[0]!.name, viewers[0]!.email)} is also viewing`
          : `${viewers.length} others viewing`}
      </span>
    </span>
  );
}
